/**
 * The login page renders before authenticated app context is available, so panel
 * branding is determined from the deployment hostname. Everything here is safe to
 * call before login and in non-DOM contexts.
 */

// World Panel browser-chrome colors. These mirror the $color-worldpanel-* SCSS tokens;
// SCSS vars can't be imported into JS, so the values are intentionally duplicated here.
const WORLDPANEL_MASK_ICON_COLOR = '#00a7b7'; // Safari pinned tab (gradient start)
const WORLDPANEL_TILE_COLOR = '#01363c'; // Windows tile (deep teal)

/**
 * Extracts a two-letter panel code from a hostname.
 * dev/stg: `insights-vn.stg.panel-insights.numerator.cloud`
 *          `verifiedvoices-vn.stg.panel-insights.numerator.cloud`
 * prod:    `vn.insights.numerator.com`
 *          `vn.verifiedvoices.numerator.com`
 * @param {string} [hostname] host to parse; defaults to the current window location
 * @returns {string} upper-cased panel code, or '' when none matches or there is no DOM
 */
export const getPanelFromHostname = (hostname) => {
  const host =
    hostname ?? (typeof window !== 'undefined' && window.location ? window.location.hostname : '');
  if (!host) return '';
  const normalized = host.replace(/^www\./, '');
  const staged = normalized.match(/^(?:insights|verifiedvoices)-([a-z]{2})\./);
  if (staged) return staged[1].toUpperCase();
  const prod = normalized.match(/^([a-z]{2})\.(?:insights|verifiedvoices)\./);
  if (prod) return prod[1].toUpperCase();
  return '';
};

/**
 * The active panel for this deployment, derived from its hostname.
 * @param {string} [hostname] host to parse; defaults to the current window location
 * @returns {string} e.g. 'VN', or '' when unknown
 */
export const getPanel = (hostname) => getPanelFromHostname(hostname);

/**
 * Whether the current deployment should render World Panel (VN) branding.
 * @param {string} [hostname] host to parse; defaults to the current window location
 * @returns {boolean}
 */
export const isWorldPanel = (hostname) => getPanel(hostname) === 'VN';

/** API document IDs for Vietnam reports whose Help Center link is unavailable. */
const WORLD_PANEL_HELP_REPORTS = [
  'wp-demographic-profile',
  'worldpanel-explorer',
  'wp-sales-change-diagnostic',
];

/**
 * Determines whether to hide the Help Center link for Worldpanel Vietnam.
 * People Group and Trip Group builders are always hidden; report links are
 * hidden when the document ID exactly matches a listed report.
 *
 * @param {Object} [options]
 * @param {string} [options.documentId] Report document identifier.
 * @param {string} [options.hostname] Hostname used to determine the active panel.
 * @param {string} [options.type] Builder type, such as 'People Group' or 'Trip Group'.
 * @returns {boolean} Whether the Help Center link should be hidden.
 */
export const shouldHideWorldPanelHelpCenter = ({ documentId, hostname, type } = {}) => {
  if (!isWorldPanel(hostname)) return false;
  if (type === 'People Group' || type === 'Trip Group') return true;

  return WORLD_PANEL_HELP_REPORTS.includes(documentId);
};

/**
 * Swaps the document favicon, title metadata, and manifest to the World Panel set.
 * Safe to call once at app boot; a no-op if there is no document/<head> or the
 * expected <head> nodes are absent.
 */
export const applyWorldPanelDocumentBranding = () => {
  if (typeof document === 'undefined' || !document.head) return;
  const set = (selector, attr, value) => {
    const el = document.head.querySelector(selector);
    if (el) el.setAttribute(attr, value);
  };
  set("link[rel='icon'][sizes='32x32']", 'href', '/favicon/wp-favicon.svg');
  set("link[rel='icon'][sizes='32x32']", 'type', 'image/svg+xml');
  set("link[rel='icon'][sizes='16x16']", 'href', '/favicon/wp-favicon.svg');
  set("link[rel='icon'][sizes='16x16']", 'type', 'image/svg+xml');
  set("link[rel='apple-touch-icon']", 'href', '/favicon/wp-apple-touch-icon.png');
  set("link[rel='mask-icon']", 'href', '/favicon/wp-favicon.svg');
  set("link[rel='mask-icon']", 'color', WORLDPANEL_MASK_ICON_COLOR);
  set("meta[name='apple-mobile-web-app-title']", 'content', 'Worldpanel Insights');
  set("meta[name='application-name']", 'content', 'Worldpanel Insights');
  set("meta[name='msapplication-TileColor']", 'content', WORLDPANEL_TILE_COLOR);
  set("link[rel='manifest']", 'href', '/manifest-worldpanel.json');
};
